/**
 * Where Cognito redirects after a login - AUTH-002, AUTH-004.
 *
 * Shared, because two things have to agree on it and neither can see the other:
 * `infra/aws-cdk/lib/auth-stack.ts` registers the callback URLs on the user pool
 * client, and `apps/web/src/auth/oauth.ts` builds the `redirect_uri` it sends.
 * A mismatch is `redirect_mismatch` on a Cognito error page rather than a
 * redirect, and each side asserting its own literal keeps both suites green
 * while that happens.
 */
export const CALLBACK_PATH = '/auth/callback';

/** The OAuth scopes the web client is registered for. Asking for another is `invalid_scope`. */
export const OAUTH_SCOPES = ['openid', 'email', 'profile'] as const;

import { z } from 'zod';

/**
 * `POST /v1/onboarding` - AUTH-010.
 *
 * The one request a caller may make before they have a profile. Every other
 * route resolves the subject claim to an `app_user` row and answers `403
 * USER_NOT_PROVISIONED` when there is none; this is what creates it.
 *
 * **`idToken` is why this exists as a body rather than a bare POST.** A Cognito
 * *access* token carries no email and no name (ADR-032), the API is denied
 * `@aws-sdk/*` so it cannot ask Cognito, and the app client's scopes do not
 * include `aws.cognito.signin.user.admin` so `GetUser` is unavailable. The id
 * token does carry them, signed by the same pool - so the server verifies it
 * against the pool's JWKS and requires its `sub` to equal the access token's.
 * The client asserts nothing; it only carries the evidence.
 */
export const onboardingRequestSchema = z
  .object({
    /** The id token from the same login as the bearer token on this request. */
    idToken: z.string().min(1),
    /**
     * What to call the person. `fullname` is optional on the pool
     * (`auth-stack.ts`) and a user pool's required attributes cannot be changed
     * after creation, so most id tokens carry no `name` - this is where it comes
     * from. Falls back to the local part of the verified email when absent.
     */
    displayName: z.string().trim().min(1).max(120).optional(),
    /** Defaults to something derived from the display name. */
    workspaceName: z.string().trim().min(1).max(120).optional(),
  })
  .readonly();

export type OnboardingRequest = z.infer<typeof onboardingRequestSchema>;
